import React from 'react';
import ReactDOM from 'react-dom/client';
import { PostHogErrorBoundary, PostHogProvider } from '@posthog/react';
import { QueryClientProvider } from '@tanstack/react-query';
import AppCrashFallback from './components/common/AppCrashFallback.js';
import { Toaster } from './components/ui/sonner.js';
import App from './App.js';
import { ThemeProvider } from './hooks/useTheme.js';
import posthog, { initClientPostHog } from './lib/posthog.js';
import { queryClient } from './lib/query/client.js';
import './styles/base.css';

const VITE_PRELOAD_RECOVERY_KEY_PREFIX = 'anysync:vite-preload-reload-attempted:';

function preloadErrorMessage(payload: unknown): string | null {
  if (payload instanceof Error) {
    return payload.message;
  }

  if (typeof payload === 'string') {
    return payload;
  }

  return null;
}

function shouldRecoverFromVitePreloadError(payload: unknown): boolean {
  const message = preloadErrorMessage(payload);

  return Boolean(
    message?.includes('Unable to preload CSS for') ||
    message?.includes('Failed to fetch dynamically imported module') ||
    message?.includes('Importing a module script failed') ||
    message?.includes('error loading dynamically imported module') ||
    message?.includes('ChunkLoadError'),
  );
}

function preloadRecoveryKey(payload: unknown): string | null {
  const message = preloadErrorMessage(payload);
  return message ? `${VITE_PRELOAD_RECOVERY_KEY_PREFIX}${message}` : null;
}

function tryMarkPreloadRecoveryAttempted(key: string): boolean {
  try {
    if (window.sessionStorage.getItem(key) === '1') {
      return false;
    }

    window.sessionStorage.setItem(key, '1');
    return true;
  } catch {
    return false;
  }
}

function installVitePreloadErrorRecovery() {
  window.addEventListener('vite:preloadError', (event) => {
    const preloadEvent = event as Event & { payload?: unknown };

    if (!shouldRecoverFromVitePreloadError(preloadEvent.payload)) {
      return;
    }

    const recoveryKey = preloadRecoveryKey(preloadEvent.payload);
    if (!recoveryKey || !tryMarkPreloadRecoveryAttempted(recoveryKey)) {
      return;
    }

    event.preventDefault();
    window.location.reload();
  });
}

installVitePreloadErrorRecovery();

function schedulePostHogInit() {
  const init = () => {
    window.setTimeout(() => {
      initClientPostHog();
    }, 0);
  };

  if (typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(init);
    return;
  }

  init();
}

schedulePostHogInit();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <PostHogProvider client={posthog}>
      <PostHogErrorBoundary fallback={AppCrashFallback}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <Toaster
              position="top-right"
              richColors={false}
              closeButton
              offset={{ top: 72, left: 16, right: 16 }}
              toastOptions={{
                className: 'anysync-toast',
              }}
            />
            <App />
          </ThemeProvider>
        </QueryClientProvider>
      </PostHogErrorBoundary>
    </PostHogProvider>
  </React.StrictMode>,
);
